var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect } from "react";
import classNames from "classnames";
import ButtonComponent from "@widget/buttons/ButtonComponent";
import Input from "@widget/forms/inputs/Input";
import FormField from "@widget/forms/inputs/FormField";
import Icon from "@icon/Icon";
import LanguageSelector from "../components/LanguageSelector";
import { LoadingSpinner } from "@widget/loaders";
import { getPasswordResetDetails, getSecurityQuestions, validateSecurityQuestion } from "@core/services/auth_services";
import { $L } from "../utils/authLocalization";
import useLoadingFeedback from "@notifications/LoadingFeedback/useLoadingFeedback";
const SecurityQuestionForm = (props) => {
    const { accountUrl, accountDisplayName, currentLanguage, availableLanguages, onLanguageChange, config } = props;
    const loadingFeedback = useLoadingFeedback();
    const [hash, setHash] = useState("");
    const [questions, setQuestions] = useState([]);
    const [answers, setAnswers] = useState(["", "", "", ""]);
    const [answerErrors, setAnswerErrors] = useState([false, false, false, false]);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [loadingQuestions, setLoadingQuestions] = useState(true);
    const [validHash, setValidHash] = useState(false);
    const [success, setSuccess] = useState(false);
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const token = urlParams.get('token'); // v5 uses 'token' parameter
        if (token) {
            setHash(token);
            loadQuestions(token);
        }
        else {
            setLoadingQuestions(false);
            setError($L('uxp-core.auth.invalid-link', currentLanguage));
        }
    }, [accountUrl, currentLanguage]);
    const loadQuestions = (token) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b;
        setLoadingQuestions(true);
        try {
            // Get password reset details using new API format
            const detailsResult = yield getPasswordResetDetails(token, accountUrl);
            if (!detailsResult.successful || !detailsResult.data || detailsResult.data.status !== 'valid') {
                if (((_a = detailsResult.data) === null || _a === void 0 ? void 0 : _a.status) === 'expired') {
                    setError($L('uxp-core.auth.link-expired', currentLanguage));
                }
                else if (((_b = detailsResult.data) === null || _b === void 0 ? void 0 : _b.status) === 'invalid') {
                    setError($L('uxp-core.auth.reset-link-invalid', currentLanguage));
                }
                else {
                    setError($L('uxp-core.auth.link-expired', currentLanguage));
                }
                setLoadingQuestions(false);
                return;
            }
            // Check if security question exists
            if (!detailsResult.data.hasSecurityQuestion) {
                setError($L('uxp-core.auth.no-security-questions', currentLanguage));
                setLoadingQuestions(false);
                return;
            }
            // Check if already validated - redirect to password reset
            if (detailsResult.data.securityQuestionValidated) {
                window.location.href = `/view/auth/reset-password?token=${token}`;
                return;
            }
            // Get security questions using token/hash
            const questionsResult = yield getSecurityQuestions(token, accountUrl);
            if (questionsResult.successful && questionsResult.data) {
                const data = questionsResult.data;
                // Handle array response
                if (Array.isArray(data) && data.length > 0) {
                    // Check for errors in any question
                    const hasError = data.some(q => q.Error);
                    if (hasError) {
                        setError($L('uxp-core.auth.load-security-questions-failed', currentLanguage));
                        setLoadingQuestions(false);
                        return;
                    }
                    // Map to Question format
                    const questionsList = data.map(q => ({
                        Question: q.Question,
                        QNum: q.QNum
                    }));
                    setQuestions(questionsList);
                    setValidHash(true);
                }
                else {
                    setError($L('uxp-core.auth.no-security-questions-found', currentLanguage));
                }
            }
            else {
                setError($L('uxp-core.auth.load-security-questions-failed', currentLanguage));
            }
        }
        catch (err) {
            setError($L('uxp-core.auth.error-loading-questions', currentLanguage));
        }
        finally {
            setLoadingQuestions(false);
        }
    });
    const handleSubmit = () => __awaiter(void 0, void 0, void 0, function* () {
        // Reset error states
        setAnswerErrors([false, false, false, false]);
        setError('');
        // Check if all questions are answered
        const newErrors = [...answerErrors];
        let hasError = false;
        questions.forEach((_q, idx) => {
            var _a;
            if (!((_a = answers[idx]) === null || _a === void 0 ? void 0 : _a.trim())) {
                newErrors[idx] = true;
                hasError = true;
            }
        });
        if (hasError) {
            setAnswerErrors(newErrors);
            setError($L('uxp-core.auth.answer-all-questions', currentLanguage));
            return;
        }
        loadingFeedback.show({ message: $L('uxp-core.auth.verifying', currentLanguage) });
        try {
            // Build params dynamically based on how many questions we have
            const params = {
                Hash: hash // v5 sends Hash instead of UserKey for security
            };
            // Only add question/answer pairs that actually exist
            questions.forEach((question, idx) => {
                const answerKey = `QA${idx + 1}`;
                const qnumKey = `QNum${idx + 1}`;
                params[answerKey] = answers[idx] || '';
                params[qnumKey] = question.QNum;
            });
            const result = yield validateSecurityQuestion(params, accountUrl);
            loadingFeedback.hide();
            if (result.successful) {
                // Show success message and redirect after delay
                setSuccess(true);
                setTimeout(() => {
                    window.location.href = `/view/auth/reset-password?token=${hash}`;
                }, 2000);
            }
            else {
                setError($L('uxp-core.auth.incorrect-security-answers', currentLanguage));
                // Mark all answers as error
                setAnswerErrors(questions.map(() => true));
            }
        }
        catch (err) {
            loadingFeedback.hide();
            setError($L('uxp-core.auth.error-validating-answers', currentLanguage));
        }
    });
    const updateAnswer = (index, value) => {
        const newAnswers = [...answers];
        newAnswers[index] = value;
        setAnswers(newAnswers);
        // Clear error for this input
        if (answerErrors[index]) {
            const newErrors = [...answerErrors];
            newErrors[index] = false;
            setAnswerErrors(newErrors);
        }
        // Clear general error
        if (error) {
            setError('');
        }
    };
    // Determine form type and settings from config
    const formType = (config === null || config === void 0 ? void 0 : config.loginForm.type) || 'island-vertical';
    const isIslandHorizontal = formType === 'island-horizontal';
    const isIslandVertical = formType === 'island-vertical';
    const isFullpage = formType === 'fullpage';
    // Get active island config
    const islandHorizontalCfg = config === null || config === void 0 ? void 0 : config.loginForm.islandHorizontal;
    const islandVerticalCfg = config === null || config === void 0 ? void 0 : config.loginForm.islandVertical;
    // Get logo from layout config
    const logo = config === null || config === void 0 ? void 0 : config.layout.logo;
    /**
     * Render the form content (used in all states)
     */
    const renderFormContent = () => {
        // Loading state
        if (loadingQuestions) {
            return (_jsxs("div", { className: "uxpcore_securityquestionform__status", children: [_jsx("h2", { className: "uxpcore_securityquestionform__status-title", children: $L('uxp-core.auth.loading', currentLanguage) }), _jsx("p", { className: "uxpcore_securityquestionform__status-message", children: $L('uxp-core.auth.loading-security-questions', currentLanguage) })] }));
        }
        // Success state
        if (success) {
            return (_jsxs("div", { className: "uxpcore_securityquestionform__status", children: [_jsx("h2", { className: "uxpcore_securityquestionform__status-title", children: $L('uxp-core.auth.security-questions-validated', currentLanguage) }), _jsx("div", { className: "uxpcore_securityquestionform__success-message", children: $L('uxp-core.auth.security-questions-success-message', currentLanguage) }), _jsx("div", { className: "uxpcore_securityquestionform__status-message", children: _jsx(LoadingSpinner, { message: $L('uxp-core.auth.redirecting', currentLanguage) }) })] }));
        }
        // Invalid link state
        if (!validHash) {
            return (_jsxs("div", { className: "uxpcore_securityquestionform__status", children: [_jsx("h2", { className: "uxpcore_securityquestionform__status-title", children: $L('uxp-core.auth.invalid-link', currentLanguage) }), error && (_jsx("div", { className: "uxpcore_securityquestionform__error-message", children: error })), _jsxs("p", { className: "uxpcore_securityquestionform__status-message", children: [$L('uxp-core.auth.link-expired-message', currentLanguage), ' ', _jsx("a", { href: "/view/auth/login", className: "uxpcore_securityquestionform__link", children: $L('uxp-core.auth.login-screen', currentLanguage) }), ' ', $L('uxp-core.auth.and-try-again', currentLanguage)] })] }));
        }
        // Valid hash - show form
        return (_jsxs(_Fragment, { children: [_jsxs("div", { className: "uxpcore_securityquestionform__form-fields", children: [questions.map((q, idx) => (_jsxs(FormField, { children: [_jsx("div", { className: "uxpcore_securityquestionform__question", children: q.Question }), _jsx(Input, { type: "text", value: answers[idx], placeholder: $L('uxp-core.auth.your-answer', currentLanguage), onChange: (val) => updateAnswer(idx, val), prefix: _jsx(Icon, { icon: ['far', 'message-question'] }), fullWidth: true, isValid: answerErrors[idx] ? false : undefined })] }, idx))), error && (_jsx("div", { className: "uxpcore_securityquestionform__error-message", children: error }))] }), _jsx("div", { className: "uxpcore_securityquestionform__actions", children: _jsx(ButtonComponent, { title: $L('uxp-core.auth.submit', currentLanguage), onClick: handleSubmit, disabled: questions.some((q, idx) => { var _a; return !((_a = answers[idx]) === null || _a === void 0 ? void 0 : _a.trim()); }), className: "uxpcore_securityquestionform__submit-btn" }) })] }));
    };
    /**
     * Render island horizontal mode (single column: branding top, form below)
     */
    const renderIslandHorizontal = () => {
        var _a;
        if (!islandHorizontalCfg)
            return null;
        const title = $L('uxp-core.auth.security-questions', currentLanguage);
        const description = questions.length === 1
            ? $L('uxp-core.auth.answer-security-question-singular', currentLanguage)
            : $L('uxp-core.auth.answer-security-questions-plural', currentLanguage).replace('$count', questions.length.toString());
        return (_jsx("div", { className: classNames('uxpcore_securityquestionform__island-wrapper', {
                [`uxpcore_securityquestionform__island-wrapper--${islandHorizontalCfg.position}`]: true
            }), children: _jsxs("div", { className: classNames('uxpcore_securityquestionform__island', 'uxpcore_securityquestionform__island--horizontal', {
                    'uxpcore_securityquestionform__island--glass': islandHorizontalCfg.glassEffect
                }), children: [((_a = config === null || config === void 0 ? void 0 : config.localization) === null || _a === void 0 ? void 0 : _a.enableLanguageSelector) && availableLanguages.length > 0 && (_jsx("div", { className: "uxpcore_securityquestionform__language-selector-wrapper", children: _jsx(LanguageSelector, { languages: availableLanguages, currentLanguage: currentLanguage, onChange: onLanguageChange }) })), logo && logo.url && (_jsx("div", { className: "uxpcore_securityquestionform__branding", children: _jsx("img", { src: logo.url, alt: logo.alt, className: "uxpcore_securityquestionform__logo" }) })), validHash && !loadingQuestions && !success && (_jsxs(_Fragment, { children: [title && (_jsx("h1", { className: "uxpcore_securityquestionform__title", children: title })), description && (_jsx("p", { className: "uxpcore_securityquestionform__description", children: description }))] })), renderFormContent()] }) }));
    };
    /**
     * Render island vertical mode (two columns: branding left, form right)
     */
    const renderIslandVertical = () => {
        var _a;
        if (!islandVerticalCfg)
            return null;
        const title = $L('uxp-core.auth.security-questions', currentLanguage);
        const description = questions.length === 1
            ? $L('uxp-core.auth.answer-security-question-singular', currentLanguage)
            : $L('uxp-core.auth.answer-security-questions-plural', currentLanguage).replace('$count', questions.length.toString());
        return (_jsx("div", { className: classNames('uxpcore_securityquestionform__island-wrapper', {
                [`uxpcore_securityquestionform__island-wrapper--${islandVerticalCfg.position}`]: true
            }), children: _jsxs("div", { className: classNames('uxpcore_securityquestionform__island', 'uxpcore_securityquestionform__island--vertical', {
                    'uxpcore_securityquestionform__island--glass': islandVerticalCfg.glassEffect
                }), children: [((_a = config === null || config === void 0 ? void 0 : config.localization) === null || _a === void 0 ? void 0 : _a.enableLanguageSelector) && availableLanguages.length > 0 && (_jsx("div", { className: "uxpcore_securityquestionform__language-selector-wrapper uxpcore_securityquestionform__language-selector-wrapper--absolute", children: _jsx(LanguageSelector, { languages: availableLanguages, currentLanguage: currentLanguage, onChange: onLanguageChange }) })), _jsxs("div", { className: "uxpcore_securityquestionform__island-left", children: [logo && logo.url && (_jsx("div", { className: "uxpcore_securityquestionform__branding", children: _jsx("img", { src: logo.url, alt: logo.alt, className: "uxpcore_securityquestionform__logo" }) })), validHash && !loadingQuestions && !success && (_jsxs(_Fragment, { children: [title && (_jsx("h1", { className: "uxpcore_securityquestionform__title", children: title })), description && (_jsx("p", { className: "uxpcore_securityquestionform__description", children: description }))] }))] }), _jsx("div", { className: "uxpcore_securityquestionform__island-right", children: renderFormContent() })] }) }));
    };
    /**
     * Render fullpage mode (two-column layout)
     */
    const renderFullpage = () => {
        var _a;
        const title = $L('uxp-core.auth.security-questions', currentLanguage);
        const description = questions.length === 1
            ? $L('uxp-core.auth.answer-security-question-singular', currentLanguage)
            : $L('uxp-core.auth.answer-security-questions-plural', currentLanguage).replace('$count', questions.length.toString());
        return (_jsxs("div", { className: "uxpcore_securityquestionform__fullpage", children: [((_a = config === null || config === void 0 ? void 0 : config.localization) === null || _a === void 0 ? void 0 : _a.enableLanguageSelector) && availableLanguages.length > 0 && (_jsx("div", { className: "uxpcore_securityquestionform__language-selector-wrapper uxpcore_securityquestionform__language-selector-wrapper--absolute", children: _jsx(LanguageSelector, { languages: availableLanguages, currentLanguage: currentLanguage, onChange: onLanguageChange }) })), _jsxs("div", { className: "uxpcore_securityquestionform__fullpage-left", children: [logo && logo.url && (_jsx("div", { className: "uxpcore_securityquestionform__branding", children: _jsx("img", { src: logo.url, alt: logo.alt, className: "uxpcore_securityquestionform__logo" }) })), validHash && !loadingQuestions && !success && (_jsxs(_Fragment, { children: [title && (_jsx("h1", { className: "uxpcore_securityquestionform__title", children: title })), description && (_jsx("p", { className: "uxpcore_securityquestionform__description", children: description }))] }))] }), _jsx("div", { className: "uxpcore_securityquestionform__fullpage-right", children: _jsx("div", { className: "uxpcore_securityquestionform__form-container", children: renderFormContent() }) })] }));
    };
    return (_jsxs("div", { className: "uxpcore_securityquestionform__container", children: [isIslandHorizontal && renderIslandHorizontal(), isIslandVertical && renderIslandVertical(), isFullpage && renderFullpage()] }));
};
export default SecurityQuestionForm;
